import { useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Eyebrow } from "../components/Reveal";
import { Field, inputCls, areaCls } from "../components/FormBits";
import { CONTACT } from "../data/content";
import { api, errorMessage } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setSent(true);
      setF(EMPTY);
      toast.success("Message envoyé.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Passez nous voir, ou écrivez-nous." lead="Nos bureaux sont au cœur de Nouméa. Pour une panne, préférez la page Assistance." />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5 space-y-8">
          <Eyebrow>Coordonnées</Eyebrow>
          <ul className="space-y-5 text-lg" data-testid="contact-details">
            <li className="flex gap-4"><Phone className="text-[#45ADE0] shrink-0" /><span><span className="block text-xs uppercase tracking-[0.18em] text-ink/50">Commercial & administratif</span><a href={CONTACT.phoneHref} data-testid="contact-phone-link">{CONTACT.phone}</a></span></li>
            <li className="flex gap-4"><Phone className="text-[#E3B321] shrink-0" /><span><span className="block text-xs uppercase tracking-[0.18em] text-ink/50">Support</span><a href={CONTACT.hotlineHref} data-testid="contact-support-phone-link">{CONTACT.hotline}</a></span></li>
            <li className="flex gap-4"><Mail className="text-[#45ADE0] shrink-0" /><span><a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link">{CONTACT.email}</a><br /><span className="text-ink/60 text-base">Support : <a href={`mailto:${CONTACT.supportEmail}`} data-testid="contact-support-email-link" className="underline underline-offset-4">{CONTACT.supportEmail}</a></span></span></li>
            <li className="flex gap-4"><MapPin className="text-[#45ADE0] shrink-0" /><span>{CONTACT.address}<br /><span className="text-ink/60 text-base">{CONTACT.city}</span></span></li>
          </ul>
          <div className="bg-white border border-ink/10 p-6">
            <p className="flex items-center gap-2 font-semibold"><Clock size={18} /> Horaires</p>
            <ul className="mt-4 space-y-2 text-sm">
              {CONTACT.hours.map(([d, h]) => <li key={d} className="flex justify-between gap-4"><span className="text-ink/60">{d}</span><span>{h}</span></li>)}
            </ul>
          </div>
          <iframe
            title="Plan d'accès ENOVA"
            data-testid="contact-map"
            className="w-full aspect-[4/3] border border-ink/10 grayscale"
            loading="lazy"
            src="https://www.google.com/maps?q=6+rue+Jean+Chalier,+Noum%C3%A9a,+Nouvelle-Cal%C3%A9donie&output=embed"
          />
        </div>
        <div className="lg:col-span-7">
          <Eyebrow>Formulaire</Eyebrow>
          <h2 className="mt-5 mb-10 font-display text-3xl font-extrabold">Votre message.</h2>
          {sent ? (
            <div data-testid="contact-success" className="bg-white border-2 border-[#45ADE0] p-10">
              <CheckCircle2 className="text-[#45ADE0]" size={40} />
              <h3 className="font-display text-2xl font-bold mt-5">Merci, nous revenons vers vous rapidement.</h3>
              <button data-testid="contact-new-btn" onClick={() => setSent(false)} className="btn-outline mt-6">Envoyer un autre message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom"><input data-testid="contact-name-input" className={inputCls} value={f.name} onChange={set("name")} required /></Field>
                <Field label="E-mail"><input type="email" data-testid="contact-email-input" className={inputCls} value={f.email} onChange={set("email")} required /></Field>
                <Field label="Téléphone"><input type="tel" data-testid="contact-phone-input" className={inputCls} value={f.phone} onChange={set("phone")} /></Field>
                <Field label="Sujet"><input data-testid="contact-subject-input" className={inputCls} value={f.subject} onChange={set("subject")} /></Field>
              </div>
              <Field label="Message"><textarea data-testid="contact-message-input" className={areaCls} value={f.message} onChange={set("message")} required minLength={5} /></Field>
              <button disabled={busy} data-testid="contact-submit-btn" className="btn-primary">{busy && <Loader2 className="animate-spin" size={16} />} Envoyer</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
